/**
 * api/billing.ts
 *
 * Client functions for the Catty billing API.
 *
 * Covers:
 *   - GET /api/billing/status              — current plan, subscription status, trial info
 *   - GET /api/billing/portal              — generate a Stripe Customer Portal URL
 *   - GET /api/billing/usage/telemetry     — usage telemetry aggregates (Athena-backed)
 */

import { apiGet } from './client';
import { apiPost } from './client';

export interface BillingStatus {
  plan: 'trial' | 'solo' | 'team' | 'enterprise' | null;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | null;
  allowed: boolean;
  reason: string | null;
  trialEndsAt: string | null;
  trialEmailsProcessed: number;
  hasStripeCustomer: boolean;
}

export async function getBillingStatus(): Promise<BillingStatus> {
  return apiGet<BillingStatus>('/api/billing/status');
}

export async function getBillingPortalUrl(): Promise<string> {
  const res = await apiGet<{ url: string }>('/api/billing/portal');
  return res.url;
}

export async function createCheckoutSession(
  planKey: 'solo' | 'team',
  quantity: number,
): Promise<string> {
  const res = await apiPost<{ url: string }>('/api/billing/checkout', {
    planKey,
    quantity,
  });
  return res.url;
}

export type TelemetryPeriod = 'current_month' | 'last_month' | 'all';
export type TelemetryGrouped = 'day' | 'month' | 'event_family' | 'none';

export interface TelemetryTotals {
  graph_calls_total: number;
  bedrock_calls_total: number;
  bedrock_tokens_total: number;
  emails_processed_total: number;
  estimated_cost_usd_total: number;
}

export interface TelemetryBreakdownRow {
  key: string;
  graph_calls: number;
  bedrock_calls: number;
  bedrock_tokens: number;
  emails_processed: number;
  estimated_cost_usd: number;
}

export interface BillingTelemetryResponse {
  period: TelemetryPeriod;
  grouped: TelemetryGrouped;
  scope: string;
  totals: TelemetryTotals;
  breakdown: TelemetryBreakdownRow[] | null;
  _source: { view: 'athena'; asOf: string };
}

export async function getBillingTelemetryUsage(
  period: TelemetryPeriod = 'current_month',
  grouped: TelemetryGrouped = 'none',
): Promise<BillingTelemetryResponse> {
  const params = new URLSearchParams({ period, grouped });
  return apiGet<BillingTelemetryResponse>(`/api/billing/usage/telemetry?${params.toString()}`);
}

export type AdminTelemetryGranularity = 'day' | 'month';

export interface AdminTelemetryRow {
  event_date?: string;
  year_month?: string;
  user_id: string;
  org_id: string | null;
  graph_calls_total: number;
  bedrock_calls_total: number;
  bedrock_tokens_total: number;
  emails_processed_total: number;
  estimated_cost_usd_total: number;
}

export interface AdminTelemetryResponse {
  period: TelemetryPeriod;
  granularity: AdminTelemetryGranularity;
  filter: {
    userId: string | null;
    orgId: string | null;
  };
  rows: AdminTelemetryRow[];
  row_count: number;
  _source: { view: 'athena'; asOf: string };
}

export async function getAdminTelemetryUsage(
  userId?: string,
  period: TelemetryPeriod = 'current_month',
  granularity: AdminTelemetryGranularity = 'month',
): Promise<AdminTelemetryResponse> {
  const params = new URLSearchParams({ period, granularity });
  if (userId) {
    params.append('userId', userId);
  }
  return apiGet<AdminTelemetryResponse>(`/api/admin/telemetry/usage?${params.toString()}`);
}
